import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowLeft, Settings2, Pencil, Trash2, Shield, Volume2, LogOut, Mic } from "lucide-react";
import { toast } from "sonner";
import { api, STATUS_LABEL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Switch } from "../components/ui/switch";
import { ChildForm } from "../components/ChildForm";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Avatar } from "../components/Avatar";
import { OverallSummary, LifeProgress } from "../components/parent/HallProgress";
import { SpeakProgress } from "../components/parent/SpeakProgress";
import { ArtProgress } from "../components/parent/ArtProgress";
import { PlanCard, UpgradeCard } from "../components/billing/PlanCard";

const STATUS_CLS = {
  not_started: "bg-stone-100 text-stone-400 border-stone-200",
  learning: "bg-amber-50 text-amber-700 border-amber-300",
  needs_practice: "bg-red-50 text-red-700 border-red-300",
  mastered: "bg-green-50 text-green-700 border-green-400",
};

const Stat = ({ label, value, sub, testid }) => (
  <div className="scroll-card p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
    <div className="text-4xl font-bold text-[var(--tea)] mt-2">{value}<span className="text-lg text-stone-400 font-semibold">{sub}</span></div>
  </div>
);

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-xs">
    {Object.entries(STATUS_LABEL).map(([k, v]) => <span key={k} className={`px-2 py-1 rounded-full border ${STATUS_CLS[k]}`}>{v}</span>)}
  </div>
);

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [kids, setKids] = useState([]);
  const [sel, setSel] = useState(null);
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [tab, setTab] = useState("overall");

  const loadKids = () => api.get("/children").then(({ data }) => { setKids(data); setSel((s) => s && data.find((k) => k.child_id === s) ? s : data[0]?.child_id || null); });
  useEffect(() => { loadKids(); }, []);
  useEffect(() => { if (sel) api.get(`/children/${sel}/dashboard`).then(({ data }) => setD(data)); else setD(null); }, [sel]);

  const child = kids.find((k) => k.child_id === sel);
  const saveSettings = async (patch) => {
    const settings = { ...child.settings, ...patch };
    setKids((ks) => ks.map((k) => (k.child_id === sel ? { ...k, settings } : k)));
    await api.patch(`/children/${sel}`, { settings });
    toast.success("Settings saved");
  };
  const saveChild = async (f) => { await api.patch(`/children/${sel}`, f); setEditing(false); loadKids(); toast.success("Learner updated"); };
  const delChild = async () => { await api.delete(`/children/${sel}`); setConfirmDel(false); setSel(null); loadKids(); toast.success("Learner removed"); };

  const o = d?.overview;
  const locked = !user?.access?.full;
  return (
    <div className="min-h-screen paper-bg font-en" data-testid="parent-dashboard">
      <header className="sticky top-0 z-20 bg-amber-100/90 backdrop-blur-md border-b-2 border-amber-300/80">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <button data-testid="parent-back-btn" onClick={() => nav("/profiles")} className="w-10 h-10 rounded-full bg-white border flex items-center justify-center"><ArrowLeft size={18} /></button>
          <div className="font-brush text-3xl text-[var(--vermilion)]">小書童</div>
          <div className="font-semibold text-stone-600">Parent Dashboard</div>
          <div className="flex-1" />
          <button data-testid="parent-recordings-btn" onClick={() => nav("/recordings")} className="px-4 h-10 rounded-full bg-white border-2 border-[var(--line)] text-sm font-semibold flex items-center gap-2"><Mic size={15} /> 家長錄音室</button>
          {user?.role === "admin" && <button data-testid="parent-admin-btn" onClick={() => nav("/admin")} className="px-4 h-10 rounded-full bg-[var(--ink)] text-white text-sm font-semibold flex items-center gap-2"><Shield size={15} /> Admin CMS</button>}
          <button data-testid="parent-logout-btn" onClick={() => logout().then(() => nav("/"))} className="w-10 h-10 rounded-full bg-white border flex items-center justify-center"><LogOut size={16} /></button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <PlanCard />
        <div className="flex flex-wrap gap-3" data-testid="parent-child-tabs">
          {kids.map((k) => (
            <button key={k.child_id} data-testid={`parent-child-tab-${k.child_id}`} onClick={() => setSel(k.child_id)}
              className={`flex items-center gap-2 pl-2 pr-4 h-12 rounded-full border-2 font-semibold ${sel === k.child_id ? "bg-[var(--vermilion)] text-white border-transparent" : "bg-white border-[var(--line)]"}`}>
              <span className="text-2xl w-8 h-8 inline-flex items-center justify-center align-middle"><Avatar value={k.avatar} /></span> {k.nickname}
            </button>
          ))}
          {!kids.length && <p className="text-stone-500" data-testid="parent-no-children">Create a learner profile first from the profiles page.</p>}
        </div>
        {d && o && (
          <>
            <div className="flex flex-wrap gap-2" data-testid="parent-hall-tabs">
              {[["overall", "Overall"], ["classic", "經典故事館"], ["life", "生活中文館"], ["speak", "我會說中文館"], ["art", "小書童畫坊"]].map(([k, label]) => (
                <button key={k} data-testid={`parent-hall-tab-${k}`} onClick={() => setTab(k)}
                  className={`h-11 px-5 rounded-full border-2 text-sm font-bold ${tab === k ? "bg-[var(--ink)] text-white border-transparent" : "bg-white border-[var(--line)]"}`}>{label}</button>
              ))}
            </div>
            {tab === "overall" && <OverallSummary d={d} />}
            {tab !== "overall" && locked && <UpgradeCard />}
            {tab === "life" && !locked && <LifeProgress d={d} />}
            {tab === "speak" && !locked && d.speak && <SpeakProgress s={d.speak} />}
            {tab === "art" && !locked && d.art && <ArtProgress a={d.art} childId={sel} />}
            {tab === "classic" && !locked && (<>
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <Stat testid="stat-zhuyin" label="Zhuyin Mastery" value={o.zhuyin_mastered} sub={` / ${o.zhuyin_total}`} />
              <Stat testid="stat-characters" label="Characters Learned" value={o.characters_learned} sub={` / ${o.characters_total}`} />
              <Stat testid="stat-stories" label="Stories Completed" value={o.stories_completed} sub={` / ${o.stories_total}`} />
              <Stat testid="stat-week-minutes" label="Learning Time This Week" value={o.week_minutes} sub=" min" />
              <Stat testid="stat-accuracy" label="Quiz Accuracy" value={o.quiz_accuracy == null ? "—" : `${Math.round(o.quiz_accuracy * 100)}%`} />
              <Stat testid="stat-tracing" label="Tracing Completed" value={o.tracing_count} />
              <Stat testid="stat-stars" label="Stars" value={o.stars} />
              <Stat testid="stat-total-minutes" label="Total Learning" value={o.total_minutes} sub=" min" />
            </section>
            <section className="grid lg:grid-cols-[2fr_1fr] gap-6">
              <div className="scroll-card p-6">
                <h2 className="text-lg font-bold mb-4">Daily minutes (last 7 days)</h2>
                <div className="h-56">
                  <ResponsiveContainer>
                    <BarChart data={d.daily_minutes.map((x) => ({ ...x, day: x.date.slice(5) }))}>
                      <XAxis dataKey="day" fontSize={12} /><YAxis fontSize={12} allowDecimals={false} /><Tooltip />
                      <Bar dataKey="minutes" fill="#C83E2B" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="scroll-card p-6" data-testid="recommended-review">
                <h2 className="text-lg font-bold">Recommended Review</h2>
                <p className="text-sm text-stone-500 mb-4">Auto-picked from lowest accuracy items.</p>
                <div className="text-xs font-semibold text-stone-500 uppercase">Zhuyin</div>
                <div className="flex flex-wrap gap-2 mt-2 mb-4">{d.recommended.zhuyin.length ? d.recommended.zhuyin.map((z) => <span key={z} className="w-12 h-12 rounded-xl bg-red-50 border-2 border-red-200 text-2xl font-black flex items-center justify-center">{z}</span>) : <span className="text-sm text-stone-400">Nothing to review yet</span>}</div>
                <div className="text-xs font-semibold text-stone-500 uppercase">Characters</div>
                <div className="flex flex-wrap gap-2 mt-2">{d.recommended.characters.length ? d.recommended.characters.map((c) => <span key={c} className="w-12 h-12 rounded-xl bg-red-50 border-2 border-red-200 font-serif-tc text-2xl font-black flex items-center justify-center">{c}</span>) : <span className="text-sm text-stone-400">Nothing to review yet</span>}</div>
              </div>
            </section>
            <section className="scroll-card p-6" data-testid="zhuyin-progress-grid">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5"><h2 className="text-lg font-bold">Zhuyin Progress</h2><Legend /></div>
              <div className="grid grid-cols-6 sm:grid-cols-10 lg:grid-cols-[repeat(13,minmax(0,1fr))] gap-2">
                {d.zhuyin.map((z) => (
                  <div key={z.symbol} title={`${STATUS_LABEL[z.status]}${z.accuracy != null ? ` · ${Math.round(z.accuracy * 100)}%` : ""}`} data-testid={`zhuyin-status-${z.symbol}`}
                    className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center ${STATUS_CLS[z.status]}`}>
                    <span className="text-2xl font-black">{z.symbol}</span>
                    <span className="text-[10px] font-semibold">{z.status === "mastered" ? "✓" : z.accuracy != null ? `${Math.round(z.accuracy * 100)}%` : ""}</span>
                  </div>
                ))}
              </div>
            </section>
            <section className="grid lg:grid-cols-[2fr_1fr] gap-6">
              <div className="scroll-card p-6 overflow-hidden" data-testid="character-progress-table">
                <h2 className="text-lg font-bold mb-3">Character Progress</h2>
                <div className="max-h-[420px] overflow-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Character</TableHead><TableHead>Zhuyin</TableHead><TableHead>Meaning</TableHead><TableHead>Accuracy</TableHead><TableHead>Practice</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {d.characters.map((c) => (
                        <TableRow key={c.character}>
                          <TableCell className="font-serif-tc text-2xl font-bold">{c.character}</TableCell>
                          <TableCell>{c.zhuyin}</TableCell>
                          <TableCell className="text-stone-500 text-sm">{c.meaning}</TableCell>
                          <TableCell>{c.accuracy == null ? "—" : `${Math.round(c.accuracy * 100)}%`}</TableCell>
                          <TableCell>{c.practice_count}</TableCell>
                          <TableCell><span className={`px-2 py-1 rounded-full border text-xs ${STATUS_CLS[c.status]}`}>{STATUS_LABEL[c.status]}</span></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <div className="space-y-6">
                <div className="scroll-card p-6" data-testid="lesson-progress-list">
                  <h2 className="text-lg font-bold mb-3">Stories</h2>
                  <div className="space-y-2">
                    {d.lessons.map((l) => (
                      <div key={l.story_id} className="flex items-center justify-between text-sm">
                        <span><span className="text-stone-400 mr-2">{l.number}.</span><span className="font-serif-tc font-bold">{l.title}</span></span>
                        <span className={l.status === "completed" ? "text-green-700 font-semibold" : l.status === "in_progress" ? "text-amber-700" : "text-stone-400"}>
                          {l.status === "completed" ? "★".repeat(l.stars) : l.status === "in_progress" ? "In progress" : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            </>)}
            <section className="max-w-xl">
                <div className="scroll-card p-6 space-y-4" data-testid="learning-settings">
                  <h2 className="text-lg font-bold flex items-center gap-2"><Settings2 size={18} /> Learning settings</h2>
                  <label className="flex items-center justify-between"><span className="flex items-center gap-2"><Volume2 size={16} /> Audio on</span>
                    <Switch data-testid="setting-audio-switch" checked={child?.settings?.audio_enabled !== false} onCheckedChange={(v) => saveSettings({ audio_enabled: v })} /></label>
                  <label className="flex items-center justify-between"><span>Auto-play narration</span>
                    <Switch data-testid="setting-autoplay-switch" checked={child?.settings?.autoplay_narration !== false} onCheckedChange={(v) => saveSettings({ autoplay_narration: v })} /></label>
                  <div className="flex gap-2 pt-2">
                    <button data-testid="edit-child-btn" onClick={() => setEditing(true)} className="flex-1 h-10 rounded-full border-2 border-[var(--line)] text-sm font-semibold flex items-center justify-center gap-2"><Pencil size={14} /> Edit</button>
                    <button data-testid="delete-child-btn" onClick={() => setConfirmDel(true)} className="flex-1 h-10 rounded-full border-2 border-red-200 text-red-600 text-sm font-semibold flex items-center justify-center gap-2"><Trash2 size={14} /> Remove</button>
                  </div>
                </div>
            </section>
          </>
        )}
      </main>
      <ChildForm open={editing} onOpenChange={setEditing} initial={child} onSubmit={saveChild} />
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="font-en">
          <AlertDialogHeader><AlertDialogTitle>Remove {child?.nickname}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes this learner's profile and all progress.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-child-btn" onClick={delChild}>Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
